import {
  applySnapshot,
  flow,
  getRoot,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { AssignEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/assignEmailCycleRPC";
import { getAPIClient } from "../../networking/APIClient";
import { RootStore } from "../../root/store/RootStore";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { useAssignEmailCycleRPCClientImpl } from "../rpcs/RPC";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";

type AssignEmailCycleRPCError = Exclude<
  AssignEmailCycleRPC.RPCError,
  | AssignEmailCycleRPC.RPCError.PendingSurveyAssignment
  | AssignEmailCycleRPC.RPCError.MissingEmailId
>;

type AssignEmailCycleRPCResult = LeoRPCResult<
  AssignEmailCycleRPC.Response,
  AssignEmailCycleRPC.Errors.Errors
>;

type AssignEmailCycleActionReturnType = Generator<
  Promise<AssignEmailCycleRPCResult>,
  boolean,
  AssignEmailCycleRPCResult
>;

const getInitialState = (): SnapshotIn<typeof AssignEmailCycleStore> => ({
  scheduleIds: [],
  loadingAssignEmailCycle: false,
  assignEmailCycleRPCError: null,
});

export const AssignEmailCycleStore = types
  .model("AssignEmailCycleStore", {
    scheduleIds: types.array(types.string),
    loadingAssignEmailCycle: types.boolean,
    assignEmailCycleRPCError: types.maybeNull(
      types.enumeration(
        "AssignEmailCycleRPCError",
        Object.values(AssignEmailCycleRPC.RPCError).filter(
          (rpcError): rpcError is AssignEmailCycleRPCError =>
            AssignEmailCycleRPC.RPCError.PendingSurveyAssignment !== rpcError &&
            AssignEmailCycleRPC.RPCError.MissingEmailId !== rpcError,
        ),
      ),
    ),
  })
  .actions((store) => ({
    toggleScheduleId: (scheduleId: string): void => {
      if (store.scheduleIds.includes(scheduleId)) {
        store.scheduleIds.remove(scheduleId);
      } else {
        store.scheduleIds.push(scheduleId);
      }
    },
    clearScheduleIds: (): void => {
      store.scheduleIds.clear();
    },
    assignEmailCycle: flow(function* (): AssignEmailCycleActionReturnType {
      store.loadingAssignEmailCycle = true;
      store.assignEmailCycleRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const respondentStore =
          getRoot<typeof RootStore>(store).projectsRootStore.projectDetailsStore
            .respondentStore;
        const request = new AssignEmailCycleRPC.Request(
          respondentStore.respondentSelection,
          store.scheduleIds.map((scheduleId) => new LeoUUID(scheduleId)),
        );

        const result =
          yield useAssignEmailCycleRPCClientImpl(apiClient).execute(request);

        if ("response" in result) return true;

        if (
          result.error instanceof
          AssignEmailCycleRPC.Errors.PendingSurveyAssignment
        ) {
          respondentStore.createOrAssignEmailCycleStore.setPendingSurveyAssignmentError(
            {
              respondentsAssigned: result.error.respondentsAssigned,
              respondentsSelected: result.error.respondentsSelected,
              respondentsUnassigned: result.error.respondentsUnassigned,
            },
          );
          return false;
        }

        if (result.error instanceof AssignEmailCycleRPC.Errors.MissingEmailId) {
          respondentStore.createOrAssignEmailCycleStore.setMissingEmailIdError(
            result.error.count,
          );
          return false;
        }

        switch (result.error.code) {
          case AssignEmailCycleRPC.RPCError.InvalidProjectId:
          case AssignEmailCycleRPC.RPCError.ProjectAlreadyArchived:
          case AssignEmailCycleRPC.RPCError.InvalidScheduleId:
          case AssignEmailCycleRPC.RPCError.EmailCycleInactive:
          case AssignEmailCycleRPC.RPCError.EmailCycleInProgress:
            store.assignEmailCycleRPCError = result.error.code;
            break;
          case AssignEmailCycleRPC.RPCError.PendingSurveyAssignment:
          case AssignEmailCycleRPC.RPCError.MissingEmailId:
            // Already handled above
            break;
          default:
            result.error.code satisfies never;
        }
        return false;
      } finally {
        store.loadingAssignEmailCycle = false;
      }
    }),
    resetRpcError: (): void => {
      store.assignEmailCycleRPCError = null;
    },
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createAssignEmailCycleStore = (): Instance<
  typeof AssignEmailCycleStore
> => AssignEmailCycleStore.create(getInitialState());
